import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  FunnelIcon,
  CalendarIcon,
  StarIcon,
  VideoCameraIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';

export default function Results() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    jobType: 'all',
    dateRange: 'all',
    category: 'all',
    callType: 'all'
  });
  const [selectedResult, setSelectedResult] = useState(null);

  useEffect(() => {
    loadResults();
  }, [filters]);

  const loadResults = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams(filters);
      const response = await fetch(`/api/v1/zoomfabric/results?${queryParams}`);
      if (response.ok) {
        const data = await response.json();
        setResults(data.results || []);
      }
    } catch (err) {
      console.error('Failed to load results:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportResults = async (format = 'csv') => {
    try {
      const queryParams = new URLSearchParams({ ...filters, format });
      const response = await fetch(`/api/v1/zoomfabric/results/export?${queryParams}`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `zoomfabric-results-${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Failed to export results:', err);
    }
  };

  const renderScore = (score) => {
    const percentage = (score / 5) * 100;
    const color = percentage >= 80 ? 'text-green-600' :
                  percentage >= 60 ? 'text-yellow-600' : 'text-red-600';

    return (
      <div className={`flex items-center ${color}`}>
        <StarIcon className="w-4 h-4 mr-1" />
        <span className="font-medium">{score.toFixed(1)}/5.0</span>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Analysis Results</h1>
            <p className="text-lg text-gray-600 mt-2">
              View and export your call analysis results
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => exportResults('csv')}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center"
            >
              <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
              Export CSV
            </button>
            <button
              onClick={() => exportResults('excel')}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
            >
              <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
              Export Excel
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Filters</h3>
            <FunnelIcon className="w-5 h-5 text-gray-400" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Job Type</label>
              <select
                value={filters.jobType}
                onChange={(e) => setFilters({...filters, jobType: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Types</option>
                <option value="categorization">Categorization</option>
                <option value="grading">Grading</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Call Type</label>
              <select
                value={filters.callType}
                onChange={(e) => setFilters({...filters, callType: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">Meetings + Phone</option>
                <option value="meeting">Meetings Only</option>
                <option value="phone_call">Phone Calls Only</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
              <select
                value={filters.dateRange}
                onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Time</option>
                <option value="last-week">Last Week</option>
                <option value="last-month">Last Month</option>
                <option value="last-quarter">Last Quarter</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={filters.category}
                onChange={(e) => setFilters({...filters, category: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Categories</option>
                <option value="sales">Sales</option>
                <option value="support">Support</option>
                <option value="training">Training</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results Dashboard */}
      {!loading && results.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <ChartBarIcon className="w-8 h-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{results.length}</p>
                <p className="text-sm text-gray-600">Total Analyzed Calls</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <StarIcon className="w-8 h-8 text-orange-500" />
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">
                  {(results.reduce((sum, r) => sum + (r.overallScore || 0), 0) / results.length).toFixed(1)}
                </p>
                <p className="text-sm text-gray-600">Average Score</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <CalendarIcon className="w-8 h-8 text-green-500" />
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(results.map(r => r.date?.split('T')[0])).size}
                </p>
                <p className="text-sm text-gray-600">Days with Data</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading results...</p>
          </div>
        ) : results.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type & Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {results.map((result, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {result.type === 'meeting' ? (
                          <VideoCameraIcon className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" />
                        ) : (
                          <PhoneIcon className="w-4 h-4 text-indigo-500 mr-2 flex-shrink-0" />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {result.type === 'meeting'
                              ? (result.meetingTitle || 'Untitled Meeting')
                              : (result.callTitle || 'Phone Call')
                            }
                          </div>
                          <div className="text-sm text-gray-500">
                            {result.type === 'meeting'
                              ? `Host: ${result.host || 'Unknown'}`
                              : `${result.caller} → ${result.callee}`
                            }
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {result.date ? new Date(result.date).toLocaleDateString() : 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {result.category || 'Uncategorized'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {result.overallScore ? renderScore(result.overallScore) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {result.duration || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => setSelectedResult(result)}
                        className="text-blue-600 hover:text-blue-800 flex items-center"
                      >
                        <EyeIcon className="w-4 h-4 mr-1" />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <ChartBarIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
            <p className="text-gray-500">Run some analysis jobs to see results here</p>
          </div>
        )}
      </div>

      {/* Result Detail Modal */}
      {selectedResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Call Analysis Details</h2>
                <button
                  onClick={() => setSelectedResult(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Meeting Title</h3>
                    <p className="text-gray-900">{selectedResult.meetingTitle || 'Untitled'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Date & Time</h3>
                    <p className="text-gray-900">
                      {selectedResult.date ? new Date(selectedResult.date).toLocaleString() : 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Category</h3>
                    <p className="text-gray-900">{selectedResult.category || 'Uncategorized'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Overall Score</h3>
                    {selectedResult.overallScore ? renderScore(selectedResult.overallScore) : <span>-</span>}
                  </div>
                </div>

                {selectedResult.gradingDetails && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Detailed Scores</h3>
                    <div className="space-y-3">
                      {Object.entries(selectedResult.gradingDetails).map(([criterion, details]) => (
                        <div key={criterion} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-gray-900">{criterion}</h4>
                            <span className="text-lg font-bold text-blue-600">{details.score}/5</span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{details.feedback}</p>
                          <div className="text-xs text-gray-500">
                            Confidence: {Math.round(details.confidence * 100)}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedResult.transcript && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Transcript Highlights</h3>
                    <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                      <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                        {selectedResult.transcript}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}